import React, { useEffect, useRef, useState } from "react";
import { Mic, MicOff, Pause, Play, Square, Loader2, Brain, Check, X as XIcon, AlertTriangle, ShieldCheck, FileText, Volume2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr, isUpstreamFailure } from "@/lib/api";

/** Comando clinico "invariato" / "*" → copia campi clinici dalla visita precedente.
 *  HA PRIORITÀ sul fast-path sano. */
export function isInvariatoCommand(raw) {
  const s = String(raw || "").toLowerCase().trim().replace(/["'`]/g, "").replace(/[.,;!?]+$/g, "");
  if (!s) return false;
  // "*" da solo o quasi
  if (s === "*" || s === "**" || s === "***") return true;
  if (s.length <= 6 && /^\*+\s*$/.test(s)) return true;
  // Frasi trigger
  const TRIGGERS = [
    "invariato",
    "invariata",
    "invariato rispetto alla precedente visita",
    "invariata rispetto alla precedente visita",
    "invariato rispetto alla precedente",
    "come visita precedente",
    "come la visita precedente",
    "come precedente",
    "come la precedente",
    "come prima",
  ];
  for (const t of TRIGGERS) {
    if (s === t || s.startsWith(t + " ") || s.startsWith(t + ",") || s.endsWith(" " + t) || s.includes(" " + t + " ")) return true;
  }
  return false;
}

/** Heuristica: testo manuale → applica template "visita sana" senza chiamare IA.
 *  Regole:
 *   - Testo breve (≤80 char) e contiene almeno una frase chiave sana.
 *   - Nessuna parola che indichi sintomo/patologia.
 *  Ritorna {anamnesi, OD, OS, tags_proposti} compatibile col parsed IA, oppure null. */
function tryHealthyTemplate(raw, defaults) {
  const s = String(raw || "").toLowerCase().trim().replace(/["'`]/g, "").replace(/[.,;!?]+$/g, "");
  if (!s) return null;
  if (s.length > 120) return null;
  const HEALTHY_PHRASES = [
    // Globali (occhio sano)
    "tutto bene", "tutto ok", "tutto a posto", "tutto normale", "tutto nella norma",
    "tutto regolare", "tutto sano", "tutto sani",
    "visita ok", "visita normale", "visita nella norma", "visita sana",
    "occhi ok", "occhi a posto", "occhi sani", "occhio sano",
    "nessun problema", "nessun disturbo", "nessuna patologia", "nessuna patologia evidente",
    "controllo normale", "controllo regolare", "controllo ok",
    "esame obiettivo nella norma", "esame obiettivo normale", "esame obiettivo ok",
    // Fondo oculare
    "fondo nella norma", "fondo oculare nella norma", "fondo oculare normale",
    "fondo ok", "fondo oculare ok", "fondo sano", "fondo oculare sano",
    "fo nella norma", "fo ok", "fo sano",
    // Segmento anteriore
    "segmento anteriore nella norma", "segmento anteriore normale",
    "segmento anteriore ok", "segmento anteriore sano",
    "sa nella norma", "sa ok", "sa sano",
    // Frasi composte sane
    "fondo nella norma e segmento anteriore ok", "fondo ok e segmento anteriore ok",
    "fondo oculare sano e segmento anteriore sano",
    "annessi sani", "pupille nella norma", "pupille normali", "pupille eucicliche",
    // Monosillabi
    "ok", "bene", "normale", "nella norma", "regolare", "sano", "sani",
  ];
  const SYMPTOMS = [
    "dolore", "occhio rosso", "occhi rossi", "rossore", "bruciore", "prurito",
    "calo visivo", "annebbiamento", "annebbia", "miodesopsie", "mosche volanti",
    "fotofobia", "lacrimazione", "secrezione", "infiammazione", "congiuntivite",
    "glaucoma", "cataratta", "retinopatia", "maculopatia", "amd",
    "ulcera", "trauma", "lesione", "edema", "emorragia", "diplopia",
    "scotoma", "metamorfopsia", "calo del visus", "calo visus",
    "headache", "mal di testa", "pressione alta", "ipertono",
    // Patologie oculistiche comuni — bloccano il fast-path sano
    "pterigio", "pterygium", "calazio", "orzaiolo", "blefarite",
    "cheratite", "cheratocono", "uveite", "iridociclite",
    "distacco di retina", "distacco retina",
    "occhio secco", "secchezza oculare", "cheratocongiuntivite secca", "dry eye",
    "neoformazione", "neoformazioni",
  ];
  for (const sym of SYMPTOMS) if (s.includes(sym)) return null;
  let matched = false;
  for (const ph of HEALTHY_PHRASES) {
    if (s === ph || s.startsWith(ph + " ") || s.endsWith(" " + ph) || s.includes(" " + ph + " ")) {
      matched = true; break;
    }
  }
  if (!matched) return null;
  const sa = (defaults && defaults.segmento_anteriore) || "mezzi diottrici trasparenti";
  const fo = (defaults && defaults.fundus) || "disco ottico roseo e a margini netti; macula e media periferia nella norma; vasi regolari per calibro e decorso; retina piana.";
  const annessi = (defaults && defaults.annessi_palpebre) || "sani";
  const pupille = (defaults && defaults.pupille) || "eucicliche e normoreagenti alla luce";
  const anamnesi = (defaults && defaults.anamnesi) || "Non segnala sintomi oculari degni di nota.";
  const diagnosi_sana = (defaults && defaults.diagnosi_sana) || "Esame obiettivo nella norma";
  const protocollo_sano = (defaults && defaults.protocollo_sano) || "Spiegato al paziente il quadro clinico nella norma. Si consiglia controllo periodico annuale tra 12 mesi, salvo insorgenza di nuovi disturbi quali dolore, arrossamento persistente, calo visivo, lampi, miodesopsie o altri sintomi oculari.";
  const eyeFields = {
    visus_naturale: "10/10",
    tono: "12",
    annessi_palpebre: annessi,
    segmento_anteriore: sa,
    pupille: pupille,
    fundus: fo,
  };
  return {
    anamnesi: anamnesi,
    OD: { ...eyeFields },
    OS: { ...eyeFields },
    tags_proposti: [],
    hashtags: [],
    speaker_segments: [],
    conflicts: [],
    diagnosi_suggerite: [
      { diagnosi: diagnosi_sana },
    ],
    terapia_suggerita: "Nessuna terapia indicata.",
    protocollo_suggerito: protocollo_sano,
    prossimo_controllo_consigliato: "Controllo periodico annuale tra 12 mesi salvo insorgenza di nuovi disturbi.",
    _healthy_template: true,
  };
}

/**
 * REC button (P0) — registrazione audio + trascrizione Whisper + parsing IA.
 * Stati: idle | requesting_mic | recording | paused | processing | preview | error
 */
export default function VisitDictation({ visitId, service, currentOD, currentOS, currentAnamnesi, onApply, healthyDefaults }) {
  const [state, setState] = useState("idle");
  const [elapsed, setElapsed] = useState(0);
  const [transcript, setTranscript] = useState("");
  const [parsed, setParsed] = useState(null);
  const [selected, setSelected] = useState({});
  const [showFallback, setShowFallback] = useState(false);
  const [manualText, setManualText] = useState("");
  const [errorInfo, setErrorInfo] = useState(null); // {title, message, hint, type:'mic'|'transcribe'|'empty_audio'}
  const [audioLevel, setAudioLevel] = useState(0); // 0..100
  const [audioBytes, setAudioBytes] = useState(0);
  const [audioWarning, setAudioWarning] = useState(""); // warning soft durante rec (es. "Nessun audio…")
  const [highSensitivity, setHighSensitivity] = useState(false); // Fase 36: catena Web Audio attiva
  const [micConsentOpen, setMicConsentOpen] = useState(false);
  const [micActiveBadge, setMicActiveBadge] = useState(false);
  const [micPower, setMicPower] = useState("off"); // "off" | "preparing" | "on"
  const [micError, setMicError] = useState(""); // ultimo errore mic visibile a fianco toggle
  const [dictationMode, setDictationMode] = useState(() => {
    try { return window.localStorage.getItem("pv-dictation-mode") || "solo"; } catch { return "solo"; }
  });
  // Fase 37: diagnostica microfono visibile all'utente
  const [micDeviceLabel, setMicDeviceLabel] = useState(""); // label del dispositivo audio selezionato
  const [micTrackLive, setMicTrackLive] = useState(false);  // true se track in stato "live"
  const [maxAudioLevel, setMaxAudioLevel] = useState(0);    // peak level della sessione corrente (per gating "Elabora")
  const [lastSignalAgoMs, setLastSignalAgoMs] = useState(-1); // ms dall'ultimo campione sopra soglia (-1 = mai)
  // Fase 37B: tipo di ingresso audio realmente registrato — "raw" (mic diretto) o "boost" (via gain/compressor).
  // Se il processing AudioContext resta suspended dopo resume(), FORZA "raw" per non registrare uno stream muto.
  const [recordingInput, setRecordingInput] = useState(""); // "" | "raw" | "boost"

  const recorderRef = useRef(null);
  const rawStreamRef = useRef(null);
  // Fase 37: stream processato (uscita catena Web Audio con gain/compressor)
  const processedStreamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const startTsRef = useRef(0);
  // Fase 37: AudioContext SEPARATO per l'analyser sul RAW stream (letture reali hardware)
  const analyserAudioCtxRef = useRef(null);
  // AudioContext per la catena di processing (gain + compressor) — separato dall'analyser
  const processingAudioCtxRef = useRef(null);
  const analyserNodeRef = useRef(null);
  const rafRef = useRef(null);
  const lastBlobRef = useRef(null);
  const maxLevelRef = useRef(0);  // peak audio level durante la registrazione (0..100)
  const audioWatchdogRef = useRef(null);
  const startMsRef = useRef(0);
  // Fase 34: flag che diventa true al primo chunk audio reale ricevuto
  const firstChunkReceivedRef = useRef(false);
  // Fase 36: Web Audio processing chain refs
  const gainNodeRef = useRef(null);
  const audioDestRef = useRef(null);
  const lowSignalSinceRef = useRef(0);
  // Fase 37: gating "recording" dietro segnale audio reale
  const signalDetectedRef = useRef(false);       // true al primo audioLevel sopra soglia
  const testMicStartTsRef = useRef(0);           // ts inizio fase "testing_mic"
  const lastSignalTsRef = useRef(0);             // ts ultimo campione sopra soglia
  const micTestTimeoutRef = useRef(null);        // timer 3s per "no signal"

  const setDictationModePersist = (m) => {
    setDictationMode(m);
    try { window.localStorage.setItem("pv-dictation-mode", m); } catch {}
  };

  // Stream persistente per il toggle "Microfono ON".
  // Quando ON, lo stream resta vivo finché il medico non lo spegne, e startRec lo riusa.
  const persistMicStreamRef = useRef(null);

  const isMicStreamAlive = () => {
    const s = persistMicStreamRef.current;
    if (!s) return false;
    const tracks = s.getAudioTracks ? s.getAudioTracks() : [];
    return tracks.some((t) => t.readyState === "live" && t.enabled !== false);
  };

  const disableMic = () => {
    try { persistMicStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
    persistMicStreamRef.current = null;
    setMicPower("off");
    setMicError("");
  };

  const enableMic = async () => {
    setMicError("");
    if (!navigator.mediaDevices?.getUserMedia) {
      setMicPower("off");
      setMicError("Browser non compatibile con la registrazione audio.");
      return false;
    }
    setMicPower("preparing");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
      });
      const tracks = stream.getAudioTracks ? stream.getAudioTracks() : [];
      const live = tracks.find((t) => t.readyState === "live" && t.enabled !== false);
      if (!live) {
        try { tracks.forEach((t) => t.stop()); } catch {}
        setMicPower("off");
        setMicError("Microfono autorizzato ma non attivo. Ricarica la pagina e riprova.");
        return false;
      }
      persistMicStreamRef.current = stream;
      setMicPower("on");
      setMicActiveBadge(true);
      setTimeout(() => setMicActiveBadge(false), 1500);
      toast.success("Microfono autorizzato");
      return true;
    } catch (e) {
      const name = e?.name || "";
      const denied = name === "NotAllowedError" || name === "PermissionDeniedError" || name === "SecurityError";
      const notfound = name === "NotFoundError" || name === "DevicesNotFoundError";
      setMicPower("off");
      const msg = denied
        ? "Microfono non autorizzato. Consenti l'accesso al microfono dal browser e riprova."
        : notfound
        ? "Il microfono non è collegato. Collega un microfono e riprova."
        : `Microfono non disponibile (${name || "errore"}).`;
      setMicError(msg);
      toast.error(msg, { duration: 7000 });
      return false;
    }
  };

  // Cleanup persistente al dismount del componente
  useEffect(() => () => {
    try { persistMicStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
    persistMicStreamRef.current = null;
  }, []);

  useEffect(() => () => { cleanupMedia(); }, []);

  const fmtTime = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  const cleanupMedia = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (audioWatchdogRef.current) { clearInterval(audioWatchdogRef.current); audioWatchdogRef.current = null; }
    if (micTestTimeoutRef.current) { clearTimeout(micTestTimeoutRef.current); micTestTimeoutRef.current = null; }
    firstChunkReceivedRef.current = false;
    signalDetectedRef.current = false;
    lowSignalSinceRef.current = 0;
    testMicStartTsRef.current = 0;
    lastSignalTsRef.current = 0;
    try { if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop(); } catch {}
    // Fase 36: chiudi la catena Web Audio di processing (destination stream)
    try {
      if (audioDestRef.current) {
        try { audioDestRef.current.stream.getTracks().forEach((t) => t.stop()); } catch {}
      }
    } catch {}
    audioDestRef.current = null;
    processedStreamRef.current = null;
    gainNodeRef.current = null;
    // Chiudi AudioContext di PROCESSING
    try {
      if (processingAudioCtxRef.current && processingAudioCtxRef.current.state !== "closed") {
        processingAudioCtxRef.current.close();
      }
    } catch {}
    processingAudioCtxRef.current = null;
    // Fase 37: chiudi AudioContext dell'ANALYSER (separato)
    analyserNodeRef.current = null;
    try {
      if (analyserAudioCtxRef.current && analyserAudioCtxRef.current.state !== "closed") {
        analyserAudioCtxRef.current.close();
      }
    } catch {}
    analyserAudioCtxRef.current = null;
    setHighSensitivity(false);
    setMicTrackLive(false);
    // Se mic toggle è ON, NON fermare le tracks del persistente: l'utente potrebbe voler subito rec di nuovo
    if (micPower !== "on") {
      try { rawStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
    }
    rawStreamRef.current = null;
  };

  const startTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    startTsRef.current = Date.now() - elapsed * 1000;
    timerRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTsRef.current) / 1000));
    }, 500);
  };
  const stopTimer = () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };

  const pickMime = () => {
    const candidates = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/mp4;codecs=mp4a.40.2",
      "audio/mp4",
      "audio/ogg;codecs=opus",
    ];
    for (const m of candidates) {
      if (window.MediaRecorder && MediaRecorder.isTypeSupported(m)) return m;
    }
    return "";
  };

  // Fase 37: analyser SEPARATO agganciato al RAW stream. Legge il volume hardware
  // vero (non quello post-processing). Chiamato dopo await analyserAudioCtx.resume().
  const startRawAnalyser = async (stream) => {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return false;
    let ctx;
    try {
      ctx = new AC();
      analyserAudioCtxRef.current = ctx;
    } catch { return false; }
    // resume() DEVE essere chiamato dentro il gesto click (siamo qui perché
    // startRec è invocato da onClick del bottone REC / consent modal).
    try { await ctx.resume(); } catch {}
    let analyser, src;
    try {
      src = ctx.createMediaStreamSource(stream);
      analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.4;
      src.connect(analyser);
      analyserNodeRef.current = analyser;
    } catch {
      try { ctx.close(); } catch {}
      analyserAudioCtxRef.current = null;
      return false;
    }

    const buf = new Uint8Array(analyser.fftSize);
    const SIGNAL_THRESHOLD = 6;   // 0..100 — soglia percentuale RMS
    let lastUiTs = 0;

    const tick = () => {
      if (!analyserNodeRef.current) return;
      analyser.getByteTimeDomainData(buf);
      // RMS su timeDomain (segnale centrato su 128)
      let sum = 0;
      for (let i = 0; i < buf.length; i++) {
        const d = (buf[i] - 128) / 128;
        sum += d * d;
      }
      const rms = Math.sqrt(sum / buf.length);
      const level = Math.min(100, Math.round(rms * 320)); // amp per rendere visibile parlato ambulatoriale

      const now = Date.now();
      if (now - lastUiTs >= 120) {
        setAudioLevel(level);
        lastUiTs = now;
      }
      if (level > maxLevelRef.current) {
        maxLevelRef.current = level;
        setMaxAudioLevel(level);
      }
      if (level > SIGNAL_THRESHOLD) {
        lastSignalTsRef.current = now;
        setLastSignalAgoMs(0);
        if (!signalDetectedRef.current) {
          signalDetectedRef.current = true;
          // Primo segnale REALE → avvia MediaRecorder + transizione a "recording"
          beginActualRecording();
        }
      } else if (lastSignalTsRef.current > 0) {
        setLastSignalAgoMs(now - lastSignalTsRef.current);
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return true;
  };

  // Fase 37/37B: costruisce catena Web Audio processing (gain+compressor) e avvia
  // MediaRecorder. Chiamata SOLO quando il raw analyser rileva segnale sopra soglia.
  //
  // NOTA CRITICA (Fase 37B): la catena di processing usa un AudioContext SEPARATO
  // (processingAudioCtxRef) creato in questa funzione — quindi NON necessariamente
  // dentro il gesto click iniziale dell'utente. Se il browser non applica il resume()
  // (context resta "suspended"), il MediaStreamDestination emette uno stream MUTO
  // anche se l'analyser sul RAW vede la voce.
  //
  // Regola di sicurezza: se dopo await ctx.resume() lo state NON è "running",
  // FALLBACK immediato a raw stream. Meglio registrazione RAW reale che boost muto.
  const beginActualRecording = async () => {
    const stream = rawStreamRef.current;
    if (!stream) return;
    // Se il MediaRecorder è già stato creato/avviato, non ricreare
    if (recorderRef.current && recorderRef.current.state !== "inactive") return;

    // Default: registra il RAW stream. Passiamo a "boost" solo se il processing
    // context è VERIFICATO in stato "running".
    let recordingStream = stream;
    let inputMode = "raw";
    setHighSensitivity(false);
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC && typeof MediaStreamAudioSourceNode !== "undefined") {
        const ctx = new AC();
        processingAudioCtxRef.current = ctx;
        // Attende esplicitamente la Promise di resume(). Se il browser
        // richiedeva ancora il gesto utente e il resume fallisce silenziosamente,
        // ctx.state resta "suspended" e NON usiamo la catena boost.
        try { await ctx.resume(); } catch {}
        if (ctx.state === "running" && typeof ctx.createMediaStreamDestination === "function") {
          const source = ctx.createMediaStreamSource(stream);
          const gain = ctx.createGain();
          gain.gain.value = 1.7;
          gainNodeRef.current = gain;
          let processedNode = gain;
          try {
            const comp = ctx.createDynamicsCompressor();
            comp.threshold.value = -30;
            comp.knee.value = 20;
            comp.ratio.value = 6;
            comp.attack.value = 0.003;
            comp.release.value = 0.25;
            source.connect(gain);
            gain.connect(comp);
            processedNode = comp;
          } catch {
            source.connect(gain);
          }
          const dest = ctx.createMediaStreamDestination();
          processedNode.connect(dest);
          audioDestRef.current = dest;
          processedStreamRef.current = dest.stream;
          // Verifica che dest.stream abbia almeno una audio track live prima di adottarlo
          const destTracks = dest.stream.getAudioTracks ? dest.stream.getAudioTracks() : [];
          const destLive = destTracks.some((t) => t.readyState === "live");
          if (destLive) {
            recordingStream = dest.stream;
            inputMode = "boost";
            setHighSensitivity(true);
          } else {
            // Fallback difensivo: se dest non ha track live, usa RAW.
            try { ctx.close(); } catch {}
            processingAudioCtxRef.current = null;
            audioDestRef.current = null;
            processedStreamRef.current = null;
            gainNodeRef.current = null;
          }
        } else {
          // Processing context non "running" (o browser senza createMediaStreamDestination):
          // chiudi e resta in RAW. NON creare MediaRecorder su dest.stream.
          try { ctx.close(); } catch {}
          processingAudioCtxRef.current = null;
        }
      }
    } catch {
      // Qualsiasi eccezione nella catena boost → RAW
      try { if (processingAudioCtxRef.current && processingAudioCtxRef.current.state !== "closed") processingAudioCtxRef.current.close(); } catch {}
      processingAudioCtxRef.current = null;
      audioDestRef.current = null;
      processedStreamRef.current = null;
      gainNodeRef.current = null;
      recordingStream = stream;
      inputMode = "raw";
      setHighSensitivity(false);
    }
    setRecordingInput(inputMode);

    const mime = pickMime();
    if (!mime) {
      cleanupMedia();
      setErrorInfo({
        type: "mic",
        title: "Formato audio non supportato",
        message: "Il browser non supporta nessun formato audio compatibile con il sistema di trascrizione.",
        hint: "Aggiorna il browser o usa la modalità testo."
      });
      setState("error");
      return;
    }

    let rec;
    try {
      rec = new MediaRecorder(recordingStream, { mimeType: mime });
    } catch (e) {
      cleanupMedia();
      setErrorInfo({ type: "mic", title: "MediaRecorder non disponibile", message: String(e).slice(0, 200), hint: "Usa modalità testo." });
      setState("error");
      return;
    }
    recorderRef.current = rec;
    chunksRef.current = [];
    setAudioBytes(0);

    // Fase 37: ondataavailable salva SOLO blob + bytes. NON imposta stato "recording"
    // (già impostato dopo rec.start() qui sotto, dopo il gate sul segnale RAW reale).
    rec.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) {
        chunksRef.current.push(e.data);
        setAudioBytes((b) => b + e.data.size);
        if (!firstChunkReceivedRef.current) {
          firstChunkReceivedRef.current = true;
        }
      }
    };
    rec.onstop = async () => {
      if (micPower !== "on") {
        try { rawStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
      }
      if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
      // Chiudi entrambi i context
      try { if (processingAudioCtxRef.current && processingAudioCtxRef.current.state !== "closed") processingAudioCtxRef.current.close(); } catch {}
      processingAudioCtxRef.current = null;
      try { if (analyserAudioCtxRef.current && analyserAudioCtxRef.current.state !== "closed") analyserAudioCtxRef.current.close(); } catch {}
      analyserAudioCtxRef.current = null;
      analyserNodeRef.current = null;

      const blob = new Blob(chunksRef.current, { type: mime });
      lastBlobRef.current = blob;
      // Fase 37: silenzio se blob vuoto/troppo piccolo OPPURE se il livello massimo
      // durante l'intera sessione è rimasto sotto soglia (< 8/100).
      const SIGNAL_THRESHOLD = 8;
      const silentRecording = !blob || blob.size === 0
        || (blob.size < 2000 && maxLevelRef.current < SIGNAL_THRESHOLD)
        || maxLevelRef.current < 3;
      if (silentRecording) {
        setErrorInfo({
          type: "empty_audio",
          title: "Nessun audio acquisito",
          message: "La registrazione è risultata vuota o troppo bassa (livello massimo rilevato: " + Math.round(maxLevelRef.current) + "%). Nessuna trascrizione possibile.",
          hint: "Controlla che il microfono giusto sia selezionato e che il volume di input sia adeguato, poi premi 'Riprova microfono'."
        });
        setState("error");
        return;
      }
      await processBlob(blob, mime);
    };
    rec.onerror = (ev) => {
      setErrorInfo({ type: "mic", title: "Errore durante la registrazione", message: String(ev?.error?.message || ev?.error || "Errore sconosciuto"), hint: "Riprova o usa modalità testo." });
      setState("error");
    };

    firstChunkReceivedRef.current = false;
    try { rec.start(1000); } catch (e) {
      cleanupMedia();
      setErrorInfo({ type: "mic", title: "Impossibile avviare la registrazione", message: String(e).slice(0, 200), hint: "Usa modalità testo." });
      setState("error");
      return;
    }
    // Ora è VERAMENTE recording: segnale reale + recorder attivo
    if (micTestTimeoutRef.current) { clearTimeout(micTestTimeoutRef.current); micTestTimeoutRef.current = null; }
    setElapsed(0);
    startTimer();
    startMsRef.current = Date.now();
    setState("recording");

    // Watchdog Fase 36: se durante rec il segnale cala drasticamente, warning soft
    if (audioWatchdogRef.current) clearInterval(audioWatchdogRef.current);
    lowSignalSinceRef.current = 0;
    audioWatchdogRef.current = setInterval(() => {
      const elapsedMs = Date.now() - startMsRef.current;
      const bytes = chunksRef.current.reduce((s, c) => s + (c?.size || 0), 0);
      if (bytes > 0 && elapsedMs > 3000) {
        const level = maxLevelRef.current || 0;
        if (level < 15 && bytes < 8000) {
          if (!lowSignalSinceRef.current) lowSignalSinceRef.current = Date.now();
          if (Date.now() - lowSignalSinceRef.current > 3000) {
            setAudioWarning("Microfono attivo ma segnale basso: avvicina il computer/microfono o usa un microfono esterno. Sto registrando, ma la trascrizione potrebbe essere incompleta.");
          }
        } else {
          lowSignalSinceRef.current = 0;
          setAudioWarning("");
        }
      }
    }, 1000);
  };

  const startRec = async () => {
    setErrorInfo(null);
    setAudioWarning("");
    setShowFallback(false);
    setTranscript("");
    setParsed(null);
    setSelected({});
    lastBlobRef.current = null;
    chunksRef.current = [];
    maxLevelRef.current = 0;
    setMaxAudioLevel(0);
    setAudioLevel(0);
    setLastSignalAgoMs(-1);
    signalDetectedRef.current = false;
    lastSignalTsRef.current = 0;
    setMicTrackLive(false);
    setMicDeviceLabel("");
    setRecordingInput("");

    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      setErrorInfo({
        type: "mic",
        title: "Browser non compatibile",
        message: "Il tuo browser non supporta la registrazione audio. Usa Chrome, Edge, Firefox o Safari aggiornati.",
        hint: "Puoi comunque incollare manualmente una trascrizione."
      });
      setState("error");
      return;
    }
    setState("requesting_mic");
    let stream;
    if (micPower === "on" && isMicStreamAlive()) {
      stream = persistMicStreamRef.current;
    } else {
      const preferredConstraints = {
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: { ideal: 1 },
          sampleRate: { ideal: 48000 },
        }
      };
      try {
        stream = await navigator.mediaDevices.getUserMedia(preferredConstraints);
      } catch (ePrimary) {
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
          });
        } catch (e) {
          const name = e?.name || ePrimary?.name || "";
          const denied = name === "NotAllowedError" || name === "PermissionDeniedError" || name === "SecurityError";
          const notfound = name === "NotFoundError" || name === "DevicesNotFoundError";
          setMicPower("off");
          persistMicStreamRef.current = null;
          setErrorInfo({
            type: "mic",
            title: denied ? "Microfono non autorizzato" : notfound ? "Microfono non collegato" : "Microfono non disponibile",
            message: denied
              ? "Microfono non autorizzato. Consenti l'accesso al microfono dal browser e riprova."
              : notfound
              ? "Il microfono non è collegato. Collega un microfono e riprova."
              : `Impossibile aprire il microfono: ${name || "errore sconosciuto"}.`,
            hint: denied
              ? "Su Chrome/Edge: clicca sull'icona lucchetto nella barra dell'URL → Microfono → Consenti, poi riprova."
              : notfound
              ? "Collega un microfono USB o abilita quello integrato, poi premi 'Riprova microfono'."
              : "Verifica le impostazioni audio del sistema e riprova."
          });
          setState("error");
          return;
        }
      }
    }

    rawStreamRef.current = stream;
    const tracks = stream.getAudioTracks ? stream.getAudioTracks() : [];
    const liveTrack = tracks.find((t) => t.readyState === "live" && t.enabled !== false);
    if (!liveTrack) {
      try { tracks.forEach((t) => t.stop()); } catch {}
      rawStreamRef.current = null;
      setErrorInfo({
        type: "mic",
        title: "Microfono bloccato",
        message: "Il browser ha autorizzato l'accesso ma il microfono non è attivo (track non in stato live).",
        hint: "Abilitalo dal lucchetto del browser → Microfono → Consenti, poi ricarica la pagina e riprova."
      });
      setState("error");
      return;
    }
    setMicTrackLive(true);
    setMicDeviceLabel(liveTrack.label || "Microfono predefinito");

    // Fase 37: passa a TESTING MIC. NON creare/avviare MediaRecorder qui.
    // L'analyser RAW deve verificare che il microfono capti audio reale prima
    // di transire a "recording" e prima di avviare MediaRecorder.
    setState("testing_mic");
    testMicStartTsRef.current = Date.now();

    const ok = await startRawAnalyser(stream);
    if (!ok) {
      cleanupMedia();
      setErrorInfo({
        type: "mic",
        title: "Analisi audio non disponibile",
        message: "Impossibile inizializzare il rilevamento del livello audio nel browser.",
        hint: "Aggiorna il browser (Chrome/Edge/Firefox/Safari) e riprova, oppure usa la modalità testo."
      });
      setState("error");
      return;
    }

    // Timer 3s: se nessun segnale reale rilevato, mostra errore esplicito
    if (micTestTimeoutRef.current) clearTimeout(micTestTimeoutRef.current);
    micTestTimeoutRef.current = setTimeout(() => {
      if (!signalDetectedRef.current) {
        // Ferma raw stream (non è mai stato utile) e cleanup
        try { rawStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
        rawStreamRef.current = null;
        if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
        try { if (analyserAudioCtxRef.current && analyserAudioCtxRef.current.state !== "closed") analyserAudioCtxRef.current.close(); } catch {}
        analyserAudioCtxRef.current = null;
        analyserNodeRef.current = null;
        setMicTrackLive(false);
        setErrorInfo({
          type: "empty_audio",
          title: "Microfono senza segnale",
          message: "Microfono autorizzato ma non sta ricevendo audio. Controlla che sia selezionato quello giusto e che il volume di input sia adeguato.",
          hint: "Verifica dalle impostazioni del sistema o del browser (icona lucchetto → Microfono) quale dispositivo è selezionato. Prova un microfono esterno o avvicinati a quello integrato, poi riprova."
        });
        setState("error");
      }
    }, 3000);
  };

  const pauseRec = () => {
    try { recorderRef.current?.pause(); } catch {}
    // Privacy: disabilita le track audio in pausa così il browser mostra mic "muto" e non capta dati
    try { rawStreamRef.current?.getAudioTracks().forEach((t) => { t.enabled = false; }); } catch {}
    stopTimer();
    setState("paused");
  };
  const resumeRec = () => {
    try { rawStreamRef.current?.getAudioTracks().forEach((t) => { t.enabled = true; }); } catch {}
    try { recorderRef.current?.resume(); } catch {}
    startTimer();
    setState("recording");
  };
  const stopRec = () => {
    stopTimer();
    if (audioWatchdogRef.current) { clearInterval(audioWatchdogRef.current); audioWatchdogRef.current = null; }
    setAudioWarning("");
    setState("processing");
    try { recorderRef.current?.stop(); } catch {}
    // Privacy: stop tracks solo se mic toggle è OFF (altrimenti lo stream persistente resta vivo)
    if (micPower !== "on") {
      try { rawStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
    }
  };

  const cancelRec = () => {
    cleanupMedia();
    chunksRef.current = [];
    setAudioBytes(0);
    setAudioLevel(0);
    setMaxAudioLevel(0);
    setLastSignalAgoMs(-1);
    setMicTrackLive(false);
    setMicDeviceLabel("");
    setRecordingInput("");
    setElapsed(0);
    setState("idle");
  };

  const processBlob = async (blob, mime) => {
    setState("processing");
    let text = "";
    try {
      const form = new FormData();
      const ext = mime.includes("mp4") ? "mp4" : mime.includes("ogg") ? "ogg" : mime.includes("wav") ? "wav" : "webm";
      form.append("file", blob, `visit.${ext}`);
      const { data } = await api.post("/ai/transcribe", form, { headers: { "Content-Type": "multipart/form-data" } });
      text = (data?.text || "").trim();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || apiErr(e, "Trascrizione fallita");
      let title = "Trascrizione fallita";
      let hint = "Riprova oppure usa modalità testo manuale.";
      if (status === 400) title = "Audio non valido";
      else if (status === 413) { title = "File troppo grande"; hint = "Registra sessioni più brevi (max 25MB)."; }
      else if (status === 422) { title = "Audio non riconoscibile"; hint = "Parla più vicino al microfono e ripeti."; }
      else if (status === 502 || status === 503) { title = "Servizio di trascrizione non disponibile"; hint = "Riprova tra qualche istante."; }
      setErrorInfo({ type: "transcribe", title, message: String(detail).slice(0, 300), hint });
      setState("error");
      return;
    }
    if (!text) {
      setErrorInfo({
        type: "transcribe",
        title: "Trascrizione vuota",
        message: "Il sistema non ha rilevato parlato comprensibile nell'audio.",
        hint: "Riprova parlando chiaramente più vicino al microfono."
      });
      setState("error");
      return;
    }
    setTranscript(text);
    // Priorità 1: comando "invariato" / "*" → copia da visita precedente
    if (isInvariatoCommand(text)) {
      const ok = await applyInvariato(text);
      if (ok) return;
      return;
    }
    // Priorità 2: fast-path "tutto ok" / "tutto bene"
    const healthy = tryHealthyTemplate(text, healthyDefaults);
    if (healthy) {
      applyHealthyParsed(text, healthy);
      return;
    }
    await runParse(text);
  };

  const retryTranscription = async () => {
    setErrorInfo(null);
    if (lastBlobRef.current && lastBlobRef.current.size > 0) {
      const mime = lastBlobRef.current.type || "audio/webm";
      await processBlob(lastBlobRef.current, mime);
    } else {
      await startRec();
    }
  };

  const openManualFromError = () => {
    setErrorInfo(null);
    setManualText("");
    setShowFallback(true);
    setState("idle");
  };

  const applyHealthyParsed = (sourceText, healthy) => {
    // Adatta il template "sano" come se fosse il payload IA, così riusa il flusso esistente
    setTranscript(sourceText);
    setShowFallback(false);
    setParsed(healthy);
    const sel = { anamnesi: !!healthy.anamnesi };
    ["OD", "OS"].forEach((side) => {
      const e = healthy[side] || {};
      Object.keys(e).forEach((k) => { if (e[k]) sel[`${side}.${k}`] = true; });
    });
    setSelected(sel);
    setState("preview");
    toast.success("Modello visita sana applicato — controlla e modifica se necessario");
  };

  const runParse = async (text) => {
    setState("processing");
    try {
      const { data } = await api.post("/medico/visit-dictation/parse", {
        transcript: text,
        visit_id: visitId,
        service,
        current_OD: currentOD,
        current_OS: currentOS,
        current_anamnesi: currentAnamnesi,
        dictation_mode: dictationMode,
      });
      setParsed(data);
      const sel = {};
      if (data.anamnesi) sel["anamnesi"] = true;
      for (const side of ["OD", "OS"]) {
        const e = data[side] || {};
        Object.keys(e).forEach((k) => { if (e[k]) sel[`${side}.${k}`] = true; });
      }
      (data.tags_proposti || []).forEach((t, i) => { sel[`tag.${i}`] = true; });
      setSelected(sel);
      // Fase 39: se il backend ha usato il fallback locale (LLM timeout/error/JSON invalid),
      // mostra info non bloccante e prosegui verso l'anteprima modificabile.
      if (data && data._ai_fallback) {
        const reason = data._fallback_reason || "sconosciuto";
        toast.info("IA avanzata non disponibile: applicato modello locale modificabile", {
          description: `Motivo tecnico: ${reason}. Puoi comunque validare e modificare i campi in anteprima.`,
          duration: 6000,
        });
      }
      setState("preview");
    } catch (e) {
      // La response del backend ora è sempre 200 con _ai_fallback quando l'IA è giù.
      // Restano qui solo errori di rete/CF veri (5xx/HTML). Provo prima il template sano locale;
      // se non applicabile, mostro la modale con la trascrizione preservata (non si perde testo).
      const healthy = tryHealthyTemplate(text, healthyDefaults);
      if (healthy) {
        applyHealthyParsed(text, healthy);
        return;
      }
      const upstream = isUpstreamFailure(e);
      setErrorInfo({
        type: "transcribe",
        title: upstream ? "Connessione IA interrotta" : "Elaborazione IA fallita",
        message: upstream
          ? "Non riesco a contattare il servizio di parsing. La trascrizione è stata preservata: puoi aprirla in modalità testo e applicare il modello locale."
          : apiErr(e, "Errore parsing IA"),
        hint: "Riprova fra qualche istante oppure usa 'Modalità testo'.",
        manualText: text,
      });
      setState("error");
    }
  };

  // Comando "invariato" / "*" → copia clinica dalla visita precedente.
  // HA PRIORITÀ sul fast-path sano e sulla pipeline IA.
  const applyInvariato = async (sourceText) => {
    if (!visitId) {
      toast.error("Apri la visita per usare 'invariato' (no draft attivo)");
      return false;
    }
    setState("processing");
    try {
      const { data } = await api.post(`/visits/${visitId}/copy-from-previous`);
      // Costruisci un payload compatibile con onApply (come se venisse dall'IA)
      // Ricarichiamo la visita aggiornata: il backend ha già fatto $set, lasciamo che il parent ricarichi
      // Ma chiamiamo anche onApply con i campi noti per refresh immediato
      const out = {
        anamnesi: undefined,
        OD: {}, OS: {},
        tags: [],
        hashtags: [],
        diagnosi_suggerite: null,
        terapia_suggerita: null,
        protocollo_suggerito: null,
        prossimo_controllo_consigliato: null,
        _invariato_applied: true,
        _source_visit_id: data?.source_visit?.id,
        _fields_copied: data?.fields_copied || [],
      };
      onApply && onApply(out);
      toast.success("Dati clinici importati dalla visita precedente. Puoi modificare solo le differenze.");
      // Forza reload immediato dei campi clinici copiati lato backend
      try { window.dispatchEvent(new CustomEvent("pv-visit-reload-clinical", { detail: { vid: visitId } })); } catch {}
      reset();
      return true;
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.detail;
      if (status === 404) {
        toast.error(msg || "Nessuna visita precedente disponibile per questo paziente.");
      } else if (status === 400) {
        toast.error(msg || "Impossibile importare la visita precedente.");
      } else {
        toast.error(apiErr(e, "Errore importazione visita precedente"));
      }
      setState("idle");
      return false;
    }
  };

  const submitManual = async () => {
    const txt = (manualText || "").trim();
    if (!txt) { toast.error("Testo trascrizione vuoto"); return; }
    // Priorità 1: comando "invariato" / "*"
    if (isInvariatoCommand(txt)) {
      const ok = await applyInvariato(txt);
      if (ok) return;
      // Se fallisce non procede oltre (no fallback IA su "*")
      return;
    }
    // Priorità 2: fast-path "tutto ok" / "tutto bene"
    const healthy = tryHealthyTemplate(txt, healthyDefaults);
    if (healthy) {
      applyHealthyParsed(txt, healthy);
      return;
    }
    setTranscript(txt);
    setShowFallback(false);
    await runParse(txt);
  };

  const applySelected = () => {
    if (!parsed) return;
    const isHealthy = !!parsed._healthy_template;
    const out = { anamnesi: undefined, OD: {}, OS: {}, tags: [],
                  hashtags: Array.isArray(parsed.hashtags) ? parsed.hashtags : [],
                  diagnosi_suggerite: parsed.diagnosi_suggerite || null,
                  terapia_suggerita: parsed.terapia_suggerita || null,
                  protocollo_suggerito: parsed.protocollo_suggerito || null,
                  prossimo_controllo_consigliato: parsed.prossimo_controllo_consigliato || null };
    // Per il template "visita sana": NON sovrascrivere campi già compilati nella cartella
    if (selected["anamnesi"] && parsed.anamnesi) {
      if (!isHealthy || !(currentAnamnesi && currentAnamnesi.trim())) {
        out.anamnesi = parsed.anamnesi;
      }
    }
    for (const side of ["OD", "OS"]) {
      const e = parsed[side] || {};
      const cur = (side === "OD" ? currentOD : currentOS) || {};
      Object.entries(e).forEach(([k, v]) => {
        if (!v || !selected[`${side}.${k}`]) return;
        if (isHealthy && cur[k] && String(cur[k]).trim()) return;  // campo già compilato → skip
        out[side][k] = v;
      });
    }
    (parsed.tags_proposti || []).forEach((t, i) => { if (selected[`tag.${i}`]) out.tags.push(t); });
    onApply && onApply(out);
    toast.success(isHealthy ? "Modello sano applicato (solo campi vuoti)" : "Applicati alla cartella");
    reset();
  };

  const applyAll = () => {
    setSelected((p) => {
      const next = { ...p };
      if (parsed?.anamnesi) next["anamnesi"] = true;
      for (const side of ["OD", "OS"]) {
        const e = parsed?.[side] || {};
        Object.keys(e).forEach((k) => { if (e[k]) next[`${side}.${k}`] = true; });
      }
      (parsed?.tags_proposti || []).forEach((t, i) => { next[`tag.${i}`] = true; });
      return next;
    });
    setTimeout(applySelected, 0);
  };

  const reset = () => {
    cleanupMedia();
    setState("idle");
    setElapsed(0);
    setTranscript("");
    setParsed(null);
    setSelected({});
    setShowFallback(false);
    setManualText("");
    setErrorInfo(null);
    setAudioLevel(0);
    setAudioBytes(0);
    setMaxAudioLevel(0);
    setLastSignalAgoMs(-1);
    setMicTrackLive(false);
    setMicDeviceLabel("");
    setRecordingInput("");
    chunksRef.current = [];
    recorderRef.current = null;
    lastBlobRef.current = null;
    maxLevelRef.current = 0;
  };

  const conflicts = parsed?.conflicts || [];
  const hasAudioSignal = audioLevel > 8;

  return (
    <>
      {/* Mic consent gate — mostrato PRIMA di chiamare getUserMedia */}
      {micConsentOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="mic-consent-modal">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center gap-2 mb-3">
              <Mic size={20} className="text-brand-orange" />
              <h3 className="font-extrabold text-brand-navy text-lg">Consenso microfono</h3>
            </div>
            <p className="text-sm text-slate-700 leading-relaxed">
              Per registrare la visita serve il microfono. Vuoi consentire l'accesso?
            </p>
            <p className="text-xs text-slate-500 mt-2">
              Il browser ti chiederà conferma. Se l'hai già negato in precedenza, clicca sull'icona <b>lucchetto</b> nella barra dell'URL → Microfono → Consenti, poi riprova.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                data-testid="mic-consent-cancel"
                onClick={() => setMicConsentOpen(false)}
                className="px-4 py-2 rounded-lg text-sm font-bold text-slate-600 hover:bg-slate-100"
              >
                Annulla
              </button>
              <button
                type="button"
                data-testid="mic-consent-allow"
                onClick={async () => {
                  setMicConsentOpen(false);
                  const ok = await enableMic();
                  if (ok) await startRec();
                }}
                className="bg-brand-orange hover:bg-brand-orangeDark text-white font-bold px-4 py-2 rounded-lg text-sm flex items-center gap-1.5"
              >
                <Mic size={14} /> Consenti microfono
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Microfono AUTORIZZATO badge — flash dopo getUserMedia success (NON conferma segnale audio reale) */}
      {micActiveBadge && (
        <div
          data-testid="mic-active-badge"
          className="fixed bottom-32 left-6 z-40 bg-emerald-500 text-white text-xs font-bold px-3 py-2 rounded-lg shadow-lg flex items-center gap-1.5 animate-in fade-in"
        >
          <Mic size={13} /> Microfono autorizzato
        </div>
      )}

      {/* Fase 36/37: badge sensibilità + barra livello audio — visibile durante test mic e registrazione */}
      {(state === "recording" || state === "testing_mic") && (
        <div
          className="fixed bottom-32 left-6 z-40 bg-white/95 backdrop-blur border border-[#E2E8F0] rounded-lg shadow-md px-3 py-2 flex flex-col gap-1"
          data-testid="mic-sensitivity-panel"
          style={{ minWidth: 220 }}
        >
          <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">
            <Mic size={11} className={audioLevel > 8 ? "text-emerald-600" : "text-amber-500"} />
            {state === "testing_mic" ? (
              <span className="text-amber-700" data-testid="mic-testing-label">Test microfono</span>
            ) : highSensitivity ? (
              <span className="text-emerald-700" data-testid="mic-sensitivity-high">Mic alta sensibilità</span>
            ) : (
              <span className="text-slate-500" data-testid="mic-sensitivity-default">Mic standard</span>
            )}
            <span className="ml-auto text-[9px] text-slate-400" data-testid="mic-level-value">{Math.round(audioLevel)}%</span>
          </div>
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden" data-testid="mic-level-bar">
            <div
              className={`h-full transition-all ${audioLevel > 40 ? "bg-emerald-500" : audioLevel > 8 ? "bg-amber-400" : "bg-red-400"}`}
              style={{ width: `${Math.min(100, audioLevel)}%` }}
            />
          </div>
          {/* Fase 37: pannello diagnostica */}
          <div className="mt-1 border-t border-slate-100 pt-1 flex flex-col gap-0.5 text-[9px] text-slate-500" data-testid="mic-diagnostic-panel">
            <div className="flex items-center justify-between gap-1">
              <span>Dispositivo</span>
              <span className="font-bold text-slate-700 truncate max-w-[130px]" title={micDeviceLabel} data-testid="mic-diag-device">
                {micDeviceLabel || "—"}
              </span>
            </div>
            <div className="flex items-center justify-between gap-1">
              <span>Track</span>
              <span className={`font-bold ${micTrackLive ? "text-emerald-700" : "text-red-600"}`} data-testid="mic-diag-track">
                {micTrackLive ? "live" : "off"}
              </span>
            </div>
            <div className="flex items-center justify-between gap-1">
              <span>Livello</span>
              <span className="font-bold text-slate-700" data-testid="mic-diag-level">
                {Math.round(audioLevel)}% · max {Math.round(maxAudioLevel)}%
              </span>
            </div>
            <div className="flex items-center justify-between gap-1">
              <span>Ultimo segnale</span>
              <span className={`font-bold ${lastSignalAgoMs === 0 ? "text-emerald-700" : lastSignalAgoMs < 0 ? "text-red-600" : "text-amber-700"}`} data-testid="mic-diag-last-signal">
                {lastSignalAgoMs < 0 ? "mai" : lastSignalAgoMs === 0 ? "ora" : `${Math.round(lastSignalAgoMs / 100) / 10}s fa`}
              </span>
            </div>
            <div className="flex items-center justify-between gap-1">
              <span>Ingresso</span>
              <span className={`font-bold ${recordingInput === "boost" ? "text-emerald-700" : recordingInput === "raw" ? "text-slate-700" : "text-slate-400"}`} data-testid="mic-diag-input">
                {recordingInput === "boost" ? "boost attivo" : recordingInput === "raw" ? "raw" : "—"}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Dictation mode toggle */}
      {state === "idle" && (
        <div className="fixed bottom-32 left-6 z-30 hidden sm:flex flex-col gap-1.5" data-testid="dictation-mode-toggle">
          {/* Microfono ON/OFF toggle — visibile e separato dal REC */}
          <div className="bg-white border border-[#E2E8F0] rounded-lg shadow-md px-2 py-1.5 flex items-center gap-1 text-[10px]">
            <span className="uppercase tracking-wider font-bold text-slate-500 px-1">Microfono</span>
            <button
              type="button"
              data-testid="mic-toggle"
              onClick={async () => {
                if (micPower === "on") {
                  disableMic();
                } else if (micPower === "preparing") {
                  // ignore
                } else {
                  await enableMic();
                }
              }}
              disabled={micPower === "preparing"}
              title={
                micPower === "on" ? "Microfono pronto — clicca per disattivare"
                : micPower === "preparing" ? "Richiesta autorizzazione…"
                : "Clicca per attivare il microfono (consenso richiesto)"
              }
              className={`px-2.5 py-1 rounded-full font-bold flex items-center gap-1 transition ${
                micPower === "on" ? "bg-emerald-500 text-white"
                : micPower === "preparing" ? "bg-amber-300 text-amber-900"
                : "bg-slate-300 text-slate-700 hover:bg-slate-400"
              }`}
            >
              {micPower === "on" ? <><Mic size={11} /> ON</>
                : micPower === "preparing" ? <><Loader2 size={11} className="animate-spin" /> …</>
                : <><MicOff size={11} /> OFF</>}
            </button>
          </div>
          {micError && (
            <div className="bg-white border border-red-300 rounded-lg shadow-md px-2 py-1 text-[10px] text-red-700 max-w-xs leading-tight" data-testid="mic-toggle-error">
              {micError}
            </div>
          )}
          {/* Dictation mode */}
          <div className="bg-white border border-[#E2E8F0] rounded-lg shadow-md px-2 py-1.5 flex items-center gap-1 text-[10px]">
            <span className="uppercase tracking-wider font-bold text-slate-500 px-1">Modalità</span>
            <button
              type="button"
              data-testid="dictation-mode-solo"
              onClick={() => setDictationModePersist("solo")}
              className={`px-2 py-1 rounded font-bold ${dictationMode === "solo" ? "bg-brand-navy text-white" : "text-slate-600 hover:bg-slate-100"}`}
              title="Interpreta la dettatura come referto del medico."
            >
              Solo medico
            </button>
            <button
              type="button"
              data-testid="dictation-mode-qa"
              onClick={() => setDictationModePersist("qa")}
              className={`px-2 py-1 rounded font-bold ${dictationMode === "qa" ? "bg-brand-navy text-white" : "text-slate-600 hover:bg-slate-100"}`}
              title="Separazione elementare domanda/risposta, non riconoscimento biometrico della voce."
            >
              Medico-paziente
            </button>
          </div>
        </div>
      )}

      {/* Floating REC button */}
      <button
        data-testid="rec-button"
        onClick={async () => {
          if (state === "idle") {
            if (micPower === "on" && isMicStreamAlive()) {
              // Microfono già pronto → avvio diretto
              await startRec();
            } else {
              // Apri gate consenso (che attiverà il toggle + REC)
              setMicConsentOpen(true);
            }
          }
          else if (state === "recording") pauseRec();
          else if (state === "paused") resumeRec();
        }}
        className={`fixed bottom-24 left-4 w-14 h-14 sm:bottom-6 sm:left-6 sm:w-20 sm:h-20 z-40 rounded-full shadow-2xl flex flex-col items-center justify-center text-white font-extrabold transition-all
          ${state === "recording" ? "bg-red-600 animate-pulse ring-4 ring-red-300" :
            state === "paused" ? "bg-amber-500 ring-4 ring-amber-300" :
            state === "testing_mic" ? "bg-amber-500 ring-4 ring-amber-200 animate-pulse" :
            state === "processing" || state === "requesting_mic" ? "bg-slate-500" :
            state === "error" ? "bg-slate-400" :
            "bg-red-600 hover:bg-red-700 hover:scale-105"}`}
        title={
          state === "idle" ? "Registra visita IA" :
          state === "recording" ? "Pausa" :
          state === "testing_mic" ? "Test microfono in corso… parla ora" :
          state === "paused" ? "Riprendi" :
          state === "processing" ? "Elaborazione…" :
          state === "requesting_mic" ? "Autorizza microfono…" : "REC"
        }
        disabled={state === "processing" || state === "requesting_mic" || state === "testing_mic" || state === "error"}
        aria-label="Registra visita IA"
      >
        {state === "processing" || state === "requesting_mic" || state === "testing_mic" ? <Loader2 size={26} className="animate-spin" /> :
         state === "recording" ? <Pause size={26} /> :
         state === "paused" ? <Play size={26} /> :
         state === "error" ? <MicOff size={26} /> :
         <Mic size={26} />}
        <span className="text-[9px] uppercase tracking-wider mt-1">
          {state === "idle" ? "REC IA" :
           state === "recording" ? fmtTime(elapsed) :
           state === "paused" ? "Pausa" :
           state === "testing_mic" ? "Test…" :
           state === "processing" ? "AI…" :
           state === "requesting_mic" ? "Mic…" :
           state === "error" ? "Errore" : "REC IA"}
        </span>
      </button>

      {/* Controls bar quando recording/paused con VU meter */}
      {(state === "recording" || state === "paused") && (
        <div className="fixed bottom-24 left-20 sm:bottom-6 sm:left-32 z-40 bg-white border border-[#E2E8F0] rounded-2xl px-4 py-3 shadow-lg flex items-center gap-3" data-testid="rec-controls">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${state === "recording" ? "bg-red-500 animate-pulse" : "bg-amber-500"}`} />
              <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">{state === "recording" ? "Registrazione" : "In pausa"}</span>
              <span className="text-sm font-mono font-bold text-brand-navy ml-1" data-testid="rec-timer">{fmtTime(elapsed)}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Volume2 size={12} className={hasAudioSignal ? "text-emerald-600" : "text-slate-400"} />
              <div className="w-32 h-1.5 bg-slate-200 rounded-full overflow-hidden" data-testid="rec-vu-meter">
                <div
                  className={`h-full transition-all ${hasAudioSignal ? "bg-emerald-500" : "bg-slate-300"}`}
                  style={{ width: `${Math.min(100, audioLevel)}%` }}
                />
              </div>
              <span className={`text-[10px] font-bold ${hasAudioSignal ? "text-emerald-700" : "text-slate-500"}`} data-testid="rec-audio-status">
                {hasAudioSignal ? "Audio rilevato" : state === "paused" ? "—" : "Silenzio"}
              </span>
              <span className="text-[10px] text-slate-400 ml-1">{(audioBytes / 1024).toFixed(0)}KB</span>
            </div>
            {audioWarning && (
              <div className="mt-1 text-[11px] font-bold text-red-600 flex items-center gap-1" data-testid="rec-audio-warning">
                <AlertTriangle size={11} /> {audioWarning}
              </div>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <button
              data-testid="rec-stop"
              onClick={stopRec}
              disabled={maxAudioLevel < 8}
              title={maxAudioLevel < 8 ? "Nessun audio acquisito: parla vicino al microfono prima di elaborare" : "Elabora la visita con IA"}
              className={`font-bold text-xs px-3 py-1.5 rounded-full flex items-center gap-1 transition ${
                maxAudioLevel < 8
                  ? "bg-slate-300 text-slate-500 cursor-not-allowed"
                  : "bg-brand-navy hover:bg-brand-navyDark text-white"
              }`}
            >
              <Square size={11} /> {maxAudioLevel < 8 ? "Nessun audio acquisito" : "Elabora visita"}
            </button>
            <button data-testid="rec-cancel-recording" onClick={cancelRec} className="text-[10px] text-slate-500 hover:text-red-600">Annulla</button>
          </div>
        </div>
      )}

      {/* Fase 37: status "Test microfono... parla ora" mentre l'analyser cerca il primo segnale reale */}
      {state === "testing_mic" && (
        <div className="fixed bottom-6 left-32 z-40 bg-white border border-amber-300 rounded-full px-4 py-2 shadow-lg flex items-center gap-2" data-testid="rec-mic-testing">
          <Loader2 size={14} className="animate-spin text-amber-600" />
          <span className="text-xs font-bold text-amber-700">
            {micTrackLive && audioLevel === 0 && lastSignalAgoMs < 0
              ? "Microfono autorizzato ma non riceve audio…"
              : "Test microfono… parla ora"}
          </span>
        </div>
      )}

      {/* Status "Sto registrando…" durante richiesta mic */}
      {state === "requesting_mic" && (
        <div className="fixed bottom-6 left-32 z-40 bg-white border border-amber-300 rounded-full px-4 py-2 shadow-lg flex items-center gap-2" data-testid="rec-mic-requesting">
          <Loader2 size={14} className="animate-spin text-amber-600" />
          <span className="text-xs font-bold text-amber-700">Autorizza il microfono…</span>
        </div>
      )}

      {/* Status processing pannello */}
      {state === "processing" && (
        <div className="fixed bottom-6 left-32 z-40 bg-white border border-[#E2E8F0] rounded-full px-4 py-2 shadow-lg flex items-center gap-2" data-testid="rec-processing">
          <Loader2 size={14} className="animate-spin text-brand-azure" />
          <span className="text-xs font-bold text-brand-navy">Sto trascrivendo l'audio…</span>
        </div>
      )}

      {/* Floating text fallback button */}
      {state === "idle" && !showFallback && (
        <button data-testid="rec-text-fallback" onClick={() => setShowFallback(true)} className="fixed bottom-6 left-32 z-30 bg-white border border-[#E2E8F0] rounded-full px-4 py-2 shadow text-xs font-bold text-slate-600 hover:text-brand-navy">
          ⌨ Modalità testo
        </button>
      )}

      {/* ERROR MODAL — esplicito, mai silenzioso */}
      {state === "error" && errorInfo && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="rec-error-modal">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg shadow-2xl">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                {errorInfo.type === "mic" ? <MicOff className="text-red-600" size={20} /> : <AlertTriangle className="text-red-600" size={20} />}
              </div>
              <div className="flex-1">
                <h3 className="font-extrabold text-brand-navy text-lg" data-testid="rec-error-title">{errorInfo.title}</h3>
                <p className="text-sm text-slate-700 mt-1" data-testid="rec-error-message">{errorInfo.message}</p>
                {errorInfo.hint && <p className="text-xs text-slate-500 mt-2 italic" data-testid="rec-error-hint">{errorInfo.hint}</p>}
              </div>
            </div>
            <div className="flex flex-wrap justify-end gap-2 mt-4">
              <button data-testid="rec-error-close" onClick={reset} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg font-semibold">Chiudi</button>
              {errorInfo.type === "mic" && (
                <button
                  data-testid="rec-error-mic-retry"
                  onClick={() => { setErrorInfo(null); startRec(); }}
                  className="px-4 py-2 text-sm bg-amber-100 hover:bg-amber-200 text-amber-900 rounded-lg font-bold flex items-center gap-1.5"
                >
                  <Mic size={14} /> Riprova microfono
                </button>
              )}
              {errorInfo.type === "transcribe" && errorInfo.manualText && tryHealthyTemplate(errorInfo.manualText, healthyDefaults) && (
                <button
                  data-testid="rec-error-apply-healthy"
                  onClick={() => {
                    const txt = errorInfo.manualText;
                    setErrorInfo(null);
                    applyHealthyParsed(txt, tryHealthyTemplate(txt, healthyDefaults));
                  }}
                  className="px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold flex items-center gap-1.5"
                >
                  <Check size={14} /> Applica visita sana
                </button>
              )}
              <button data-testid="rec-error-manual" onClick={openManualFromError} className="px-4 py-2 text-sm bg-slate-100 hover:bg-slate-200 text-brand-navy rounded-lg font-bold">Usa trascrizione manuale</button>
              <button data-testid="rec-error-retry" onClick={retryTranscription} className="px-4 py-2 text-sm bg-brand-navy hover:bg-brand-navyDark text-white rounded-lg font-bold flex items-center gap-1.5">
                <Mic size={14} /> Riprova
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fallback manuale (volontario) */}
      {showFallback && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowFallback(false)} data-testid="rec-fallback-modal">
          <div className="bg-white rounded-xl p-5 w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-2"><Brain className="text-brand-azure" size={18} /><h3 className="font-extrabold text-brand-navy">Trascrizione manuale</h3></div>
            <p className="text-xs text-slate-500 mb-3">Incolla o scrivi la trascrizione della visita. L'IA estrarrà i campi e mostrerà un'anteprima prima di applicarli.</p>
            <textarea data-testid="rec-manual-text" rows={8} value={manualText} onChange={(e) => setManualText(e.target.value)} placeholder="es. Paziente con occhi rossi da 5 giorni…" className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm focus:outline-none focus:border-brand-azure" />
            {tryHealthyTemplate(manualText, healthyDefaults) && (
              <div className="mt-2 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1.5 flex items-center gap-1.5">
                <Check size={12} /> Testo compatibile con il modello <b>visita sana</b>. Sarà compilato automaticamente senza chiamare l'IA.
              </div>
            )}
            <div className="mt-3 flex justify-end gap-2">
              <button onClick={() => setShowFallback(false)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Annulla</button>
              <button data-testid="rec-manual-process" onClick={submitManual} className="bg-brand-navy hover:bg-brand-navyDark text-white font-bold px-4 py-2 rounded-lg text-sm flex items-center gap-1.5">
                {tryHealthyTemplate(manualText, healthyDefaults) ? <><Check size={13} /> Applica visita sana</> : <><Brain size={13} /> Elabora con IA</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview modal */}
      {state === "preview" && parsed && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-auto" data-testid="rec-preview-modal">
          <div className="bg-white rounded-2xl w-full max-w-5xl my-8 shadow-2xl overflow-hidden">
            <div className="bg-brand-navy text-white px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Brain size={18} />
                <div>
                  <div className="font-extrabold text-lg leading-tight">Anteprima dettatura IA</div>
                  <div className="text-xs text-white/70">Seleziona quali campi applicare alla cartella. Nulla viene salvato senza la tua conferma.</div>
                </div>
              </div>
              <button onClick={reset} className="hover:bg-white/10 p-1.5 rounded-lg"><XIcon size={18} /></button>
            </div>

            <div className="p-5 grid grid-cols-1 lg:grid-cols-2 gap-4 max-h-[75vh] overflow-auto">
              <div className="bg-slate-50 border border-[#E2E8F0] rounded-xl p-3" data-testid="rec-transcript-box">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-2 flex items-center gap-1.5"><FileText size={11} /> Trascrizione e speaker</div>
                {Array.isArray(parsed.speaker_segments) && parsed.speaker_segments.length > 0 ? (
                  <ul className="space-y-1.5">
                    {parsed.speaker_segments.map((s, i) => (
                      <li key={i} className="text-xs">
                        <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider font-bold mr-1.5 ${s.speaker === "medico" ? "bg-brand-azure text-white" : "bg-amber-100 text-amber-800"}`}>{s.speaker}</span>
                        <span className="text-slate-700">{s.text}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-xs text-slate-600 whitespace-pre-wrap">{transcript || "(vuota)"}</div>
                )}
                <div className="mt-3 text-[10px] text-slate-500 italic flex items-start gap-1.5">
                  <ShieldCheck size={10} className="mt-0.5" /> Audio raw e trascrizione vengono eliminati automaticamente dopo 7 giorni.
                </div>
              </div>

              <div className="space-y-3">
                {parsed.anamnesi && (
                  <FieldRow
                    testid="prev-anamnesi"
                    label="Anamnesi"
                    value={parsed.anamnesi}
                    conflict={conflicts.find((c) => c.field === "anamnesi")}
                    checked={!!selected["anamnesi"]}
                    onChange={(v) => setSelected((p) => ({ ...p, anamnesi: v }))}
                  />
                )}
                {parsed.anamnesi_dettagli && Object.values(parsed.anamnesi_dettagli).some(Boolean) && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-amber-800 mb-1">Dettagli anamnestici estratti</div>
                    <ul className="space-y-0.5 text-amber-900">
                      {Object.entries(parsed.anamnesi_dettagli).map(([k, v]) => v ? <li key={k}><b>{k}:</b> {Array.isArray(v) ? v.join("; ") : v}</li> : null)}
                    </ul>
                  </div>
                )}

                {["OD", "OS"].map((side) => {
                  const eye = parsed[side] || {};
                  const items = Object.entries(eye).filter(([_, v]) => v);
                  if (items.length === 0) return null;
                  return (
                    <div key={side} className="border border-[#E2E8F0] rounded-xl p-3" data-testid={`prev-${side}`}>
                      <div className="font-extrabold text-brand-navy text-sm mb-2">{side === "OD" ? "Occhio Destro" : "Occhio Sinistro"}</div>
                      <ul className="space-y-1.5">
                        {items.map(([k, v]) => (
                          <FieldRow
                            key={k}
                            testid={`prev-${side}-${k}`}
                            compact
                            label={k}
                            value={String(v)}
                            conflict={conflicts.find((c) => c.field === `${side}.${k}`)}
                            checked={!!selected[`${side}.${k}`]}
                            onChange={(val) => setSelected((p) => ({ ...p, [`${side}.${k}`]: val }))}
                          />
                        ))}
                      </ul>
                    </div>
                  );
                })}

                {Array.isArray(parsed.tags_proposti) && parsed.tags_proposti.length > 0 && (
                  <div className="border border-[#E2E8F0] rounded-xl p-3">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-600 mb-2">Tag proposti</div>
                    <div className="flex flex-wrap gap-1.5" data-testid="prev-tags">
                      {parsed.tags_proposti.map((t, i) => (
                        <label key={i} className={`px-2 py-1 rounded-full text-[11px] font-bold cursor-pointer transition ${selected[`tag.${i}`] ? "bg-brand-navy text-white" : "bg-white text-slate-600 border border-[#E2E8F0]"}`}>
                          <input type="checkbox" checked={!!selected[`tag.${i}`]} onChange={(e) => setSelected((p) => ({ ...p, [`tag.${i}`]: e.target.checked }))} className="hidden" />
                          {t}
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {Array.isArray(parsed.missing_data) && parsed.missing_data.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-amber-800 mb-1 flex items-center gap-1.5"><AlertTriangle size={11} /> Dati mancanti</div>
                    <ul className="list-disc list-inside text-amber-900">{parsed.missing_data.map((m, i) => <li key={i}>{m}</li>)}</ul>
                  </div>
                )}

                {conflicts.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-xs">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-red-800 mb-1 flex items-center gap-1.5"><AlertTriangle size={11} /> Conflitti con campi già pieni</div>
                    <ul className="space-y-1 text-red-900">{conflicts.map((c, i) => <li key={i}><b>{c.field}:</b> esistente "{c.current}" → nuovo "{c.new}"</li>)}</ul>
                    <div className="mt-1 text-[10px] text-red-700 italic">Per sovrascrivere il campo, lascia la spunta. Per mantenere il valore attuale, deseleziona.</div>
                  </div>
                )}

                {/* Fase 41: pannello Suggerimenti IA clinici — visibile solo al medico, NON entra nel PDF paziente */}
                {parsed.clinical_ai && (
                  <div className="border-2 border-brand-azure/40 bg-sky-50/60 rounded-xl p-3 text-xs" data-testid="clinical-ai-panel">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-brand-navy mb-2 flex items-center gap-1.5">
                      <Brain size={12} /> Suggerimenti IA clinici — <span className="text-brand-azure">solo per il medico, non nel PDF paziente</span>
                    </div>

                    {/* Diagnosi differenziale con compatibilità */}
                    {Array.isArray(parsed.clinical_ai.diagnosis_suggestions) && parsed.clinical_ai.diagnosis_suggestions.length > 0 && (
                      <div className="mb-2" data-testid="clinical-ai-diagnosis">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-700 mb-1">Diagnosi differenziale</div>
                        <ul className="space-y-1">
                          {parsed.clinical_ai.diagnosis_suggestions.map((d, i) => (
                            <li key={i} className="flex items-start gap-2 bg-white rounded px-2 py-1.5 border border-slate-200">
                              <span className={`text-[9px] font-extrabold px-1.5 py-0.5 rounded ${d.clinical_compatibility >= 85 ? "bg-emerald-100 text-emerald-800" : d.clinical_compatibility >= 60 ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700"}`} title="Compatibilità clinica (solo medico)">
                                {d.clinical_compatibility}%
                              </span>
                              <div className="flex-1">
                                <div className="font-bold text-slate-800">{d.diagnosis}</div>
                                {d.reasoning_short && <div className="text-slate-600 text-[11px] mt-0.5">{d.reasoning_short}</div>}
                                {Array.isArray(d.tags) && d.tags.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {d.tags.map((t, j) => <span key={j} className="text-[9px] bg-brand-navy/10 text-brand-navy px-1.5 py-0.5 rounded-full">{t}</span>)}
                                  </div>
                                )}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Terapia suggerita */}
                    {Array.isArray(parsed.clinical_ai.therapy_suggestions) && parsed.clinical_ai.therapy_suggestions.length > 0 && (
                      <div className="mb-2" data-testid="clinical-ai-therapy">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-700 mb-1">Terapia da validare</div>
                        <ul className="space-y-1">
                          {parsed.clinical_ai.therapy_suggestions.map((t, i) => (
                            <li key={i} className="bg-white rounded px-2 py-1.5 border border-slate-200">
                              <div className="font-bold text-slate-800">{t.therapy}</div>
                              {t.posology && <div className="text-slate-600"><b>Posologia:</b> {t.posology}</div>}
                              {t.indications && <div className="text-slate-600 text-[11px]"><b>Indicazioni:</b> {t.indications}</div>}
                              {t.contraindications && <div className="text-red-700 text-[11px] mt-0.5"><b>Controindicazioni:</b> {t.contraindications}</div>}
                              {t.why && <div className="text-slate-500 text-[11px] italic mt-0.5">{t.why}</div>}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Protocollo & Follow-up */}
                    {parsed.clinical_ai.protocol_suggestion && (
                      <div className="mb-2 bg-white rounded px-2 py-1.5 border border-slate-200" data-testid="clinical-ai-protocol">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-700 mb-0.5">Protocollo suggerito</div>
                        <div className="text-slate-700 text-[11px] leading-relaxed">{parsed.clinical_ai.protocol_suggestion}</div>
                      </div>
                    )}

                    {/* Safety notes */}
                    {Array.isArray(parsed.clinical_ai.safety_notes) && parsed.clinical_ai.safety_notes.length > 0 && (
                      <div className="mb-2 bg-amber-50 border border-amber-300 rounded px-2 py-1.5" data-testid="clinical-ai-safety">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-amber-800 mb-0.5 flex items-center gap-1"><AlertTriangle size={10} /> Note di sicurezza</div>
                        <ul className="list-disc list-inside text-amber-900 text-[11px] space-y-0.5">
                          {parsed.clinical_ai.safety_notes.map((n, i) => <li key={i}>{n}</li>)}
                        </ul>
                      </div>
                    )}

                    {/* Fonti verificabili */}
                    {Array.isArray(parsed.clinical_ai.sources_used) && parsed.clinical_ai.sources_used.length > 0 && (
                      <div data-testid="clinical-ai-sources">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-700 mb-1">Fonti</div>
                        <ul className="space-y-0.5">
                          {parsed.clinical_ai.sources_used.map((s, i) => (
                            <li key={i} className="text-[11px]">
                              {s.url ? (
                                <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-brand-azure hover:underline font-semibold">{s.title}</a>
                              ) : (
                                <span className="text-slate-500">{s.title}</span>
                              )}
                              {s.society && <span className="text-slate-500"> — {s.society}</span>}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="border-t border-[#E2E8F0] px-5 py-3 flex flex-wrap items-center justify-between gap-2 bg-slate-50">
              <div className="text-[10px] text-slate-500 italic">Confidenza IA: <b>{parsed.confidence ? Math.round(parsed.confidence * 100) + "%" : "n/d"}</b> · Il medico è responsabile della conferma finale.</div>
              <div className="flex gap-2">
                <button data-testid="rec-cancel" onClick={reset} className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-200">Annulla</button>
                <button data-testid="rec-apply-selected" onClick={applySelected} className="bg-brand-azure hover:bg-brand-azureDark text-white font-bold px-4 py-2 rounded-lg text-sm flex items-center gap-1.5"><Check size={13} /> Applica selezionati</button>
                <button data-testid="rec-apply-all" onClick={applyAll} className="bg-brand-navy hover:bg-brand-navyDark text-white font-bold px-4 py-2 rounded-lg text-sm flex items-center gap-1.5"><Check size={13} /> Applica tutto</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function FieldRow({ testid, label, value, conflict, checked, onChange, compact }) {
  return (
    <li className={`${compact ? "py-1" : "py-1.5"} flex items-start gap-2 ${conflict ? "bg-red-50 border border-red-200 rounded px-2" : ""}`}>
      <input data-testid={`${testid}-check`} type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="mt-1" />
      <div className="min-w-0 flex-1">
        <div className={`${compact ? "text-[10px]" : "text-[11px]"} uppercase tracking-wider font-bold text-slate-500`}>{label}{conflict && <span className="ml-1 text-red-600">· conflitto</span>}</div>
        <div className={`${compact ? "text-xs" : "text-sm"} text-brand-navy whitespace-pre-wrap`} data-testid={`${testid}-value`}>{value}</div>
        {conflict && <div className="text-[10px] text-red-700 italic">Esistente: {conflict.current}</div>}
      </div>
    </li>
  );
}
